import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Paperclip, Trash2, Upload } from "lucide-react";
import { type DragEvent, useState } from "react";
import { api } from "../lib/api";
import { getToken } from "../lib/auth";

interface Props {
  noteId: string;
}

export function AttachmentsPanel({ noteId }: Props) {
  const qc = useQueryClient();
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["attachments", noteId],
    queryFn: () => api.listAttachments(noteId),
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => api.uploadAttachment(file, noteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attachments", noteId] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteAttachment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attachments", noteId] }),
  });

  async function uploadFiles(files: FileList | File[]) {
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        await uploadMut.mutateAsync(f);
      }
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) {
      void uploadFiles(e.dataTransfer.files);
    }
  }

  function pickFiles() {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.multiple = true;
    inp.onchange = () => {
      if (inp.files) void uploadFiles(inp.files);
    };
    inp.click();
  }

  function downloadUrl(id: string): string {
    const t = getToken();
    return `/api/attachments/${id}/download${t ? `?_=${Date.now()}` : ""}`;
  }

  return (
    <div className="border-b border-zinc-800 p-3">
      <h3 className="mb-2 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500">
        <Paperclip className="h-4 w-4" />
        Attachments
      </h3>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={pickFiles}
        className={`mb-2 flex cursor-pointer items-center justify-center gap-1 rounded border border-dashed py-3 text-xs ${
          dragOver
            ? "border-emerald-500 bg-emerald-900/10 text-emerald-300"
            : "border-zinc-700 text-zinc-500 hover:border-zinc-600"
        }`}
      >
        <Upload className="h-3 w-3" />
        {busy ? "Lade hoch…" : "Datei droppen oder klicken"}
      </div>

      <ul className="space-y-1">
        {data?.map((a) => (
          <li
            key={a.id}
            className="group flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-zinc-900"
          >
            <FileText className="h-3 w-3 shrink-0 text-zinc-500" />
            <a
              href={downloadUrl(a.id)}
              onClick={async (e) => {
                // Force-Download via fetch+blob, weil Browser direkten /download
                // ohne Token-Header nicht authentifizieren kann.
                e.preventDefault();
                const token = getToken();
                const res = await fetch(`/api/attachments/${a.id}/download`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = a.filename;
                link.click();
                URL.revokeObjectURL(url);
              }}
              className="flex-1 truncate text-zinc-300 hover:text-emerald-400"
              title={`${a.mime} · ${(a.size_bytes / 1024).toFixed(1)} KB`}
            >
              {a.filename}
            </a>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const token = getToken();
                const res = await fetch(`/api/attachments/${a.id}/download`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              }}
              className="text-zinc-500 opacity-0 group-hover:opacity-100 hover:text-emerald-400"
              title="Öffnen"
            >
              <Download className="h-3 w-3" />
            </button>
            <button
              onClick={() => {
                if (window.confirm(`"${a.filename}" entfernen?`)) deleteMut.mutate(a.id);
              }}
              className="text-zinc-500 opacity-0 group-hover:opacity-100 hover:text-red-400"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
